import { Component, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StreamingSpeechService } from '../dictation-button/services/streaming-speech.service';
import {
  StreamingSpeechConfig,
  StreamingSpeechError,
  LLMConfig,
  TranscriptMessage,
  FinalTranscriptMessage,
  ProcessedTranscriptMessage,
} from '../dictation-button/interfaces/streaming-speech-types';

/**
 * Streaming Dictation Component
 *
 * Reusable component for streaming speech-to-text with support for:
 * - Real-time transcript display (streaming mode)
 * - Delayed transcript display (blind mode)
 * - LLM processing
 * - FormControl integration
 */
@Component({
  selector: 'app-streaming-dictation',
  templateUrl: './streaming-dictation.component.html',
  styleUrls: ['./streaming-dictation.component.scss'],
})
export class StreamingDictationComponent implements OnInit, OnDestroy {
  @Input() targetControl?: FormControl;
  @Input() processingMode: 'streaming' | 'batch' = 'streaming';
  @Input() displayMode: 'streaming' | 'blind' = 'streaming';
  @Input() maxRecordingTime?: number;
  @Input() llmConfig?: LLMConfig;
  @Input() patientId?: string;
  @Input() patientUuid?: string;
  @Input() practiceId?: string;
  @Input() practiceTenantId?: string;
  @Input() fieldContext = 'Unknown';
  @Input() componentName = 'StreamingDictation';
  @Input() formFieldName = 'unknown';
  @Input() sampleRate = 16000;

  @Output() transcriptReceived = new EventEmitter<string>();
  @Output() processedTranscriptReceived = new EventEmitter<string>();
  @Output() recordingStarted = new EventEmitter<void>();
  @Output() recordingStopped = new EventEmitter<void>();
  @Output() errorOccurred = new EventEmitter<StreamingSpeechError>();

  isRecording = false;
  isConnecting = false;
  isProcessingLLM = false;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  currentTranscript = '';
  errorMessage: string | null = null;
  warningMessage: string | null = null;

  private subscriptions: Subscription[] = [];
  private currentSessionId: string | null = null;

  constructor(private streamingSpeechService: StreamingSpeechService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Subscribe to connection status
    this.subscriptions.push(
      this.streamingSpeechService.connectionStatus$.subscribe(status => {
        this.connectionStatus = status;
        this.isConnecting = status === 'connecting';
        this.cdr.detectChanges();
      })
    );

    // Subscribe to transcripts (filtered by display mode)
    this.subscriptions.push(
      this.streamingSpeechService.transcript$.subscribe(message => {
        if (this.displayMode === 'streaming') {
          // In streaming mode, show all transcripts (interim and final)
          this.updateTranscript(message.transcript, message.isFinal);
        } else {
          // In blind mode, only show final transcripts
          if (message.isFinal) {
            this.updateTranscript(message.transcript, true);
          }
        }
      })
    );

    // Subscribe to final transcripts
    this.subscriptions.push(
      this.streamingSpeechService.finalTranscript$.subscribe(message => {
        this.updateTranscript(message.transcript, true);
      })
    );

    // Subscribe to processed transcripts (LLM output)
    this.subscriptions.push(
      this.streamingSpeechService.processedTranscript$.subscribe(message => {
        this.isProcessingLLM = false;
        this.updateProcessedTranscript(message.processedTranscript, message.rawTranscript);
      })
    );

    // Subscribe to errors
    this.subscriptions.push(
      this.streamingSpeechService.error$.subscribe(error => {
        this.handleError(error);
      })
    );

    // Subscribe to warnings
    this.subscriptions.push(
      this.streamingSpeechService.warning$.subscribe(warning => {
        this.handleWarning(warning);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.isRecording) {
      this.stopRecording();
    }
  }

  /**
   * Toggle recording
   */
  toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  /**
   * Start recording
   */
  async startRecording(): Promise<void> {
    if (!StreamingSpeechService.isBrowserSupported()) {
      this.errorMessage = 'Your browser does not support speech recognition';
      return;
    }

    try {
      this.isRecording = true;
      this.isConnecting = true;
      this.errorMessage = null;
      this.warningMessage = null;
      this.currentTranscript = '';

      // Build configuration
      const config: StreamingSpeechConfig = {
        processingMode: this.processingMode,
        sampleRate: this.sampleRate,
        patientId: this.patientId,
        patientUuid: this.patientUuid,
        practiceId: this.practiceId,
        practiceTenantId: this.practiceTenantId,
        fieldContext: this.fieldContext,
        componentName: this.componentName,
        maxRecordingTime: this.maxRecordingTime,
        llmConfig: this.llmConfig,
      };

      // Start session
      await this.streamingSpeechService.startSession(config);

      this.isConnecting = false;
      this.recordingStarted.emit();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      this.isRecording = false;
      this.isConnecting = false;
      this.errorMessage = error instanceof Error ? error.message : 'Failed to start recording';
      this.cdr.detectChanges();
    }
  }

  /**
   * Stop recording
   */
  async stopRecording(): Promise<void> {
    if (!this.isRecording) {
      return;
    }

    try {
      await this.streamingSpeechService.stopSession();
      this.isRecording = false;
      this.recordingStopped.emit();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Failed to stop recording:', error);
      this.errorMessage = error instanceof Error ? error.message : 'Failed to stop recording';
      this.isRecording = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Cancel recording
   */
  async cancelRecording(): Promise<void> {
    try {
      await this.streamingSpeechService.cancelSession('User cancelled');
      this.isRecording = false;
      this.currentTranscript = '';
      this.errorMessage = null;
      this.warningMessage = null;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Failed to cancel recording:', error);
    }
  }

  /**
   * Update transcript display
   */
  private updateTranscript(transcript: string, isFinal: boolean): void {
    if (this.displayMode === 'blind' && !isFinal) {
      // In blind mode, ignore interim transcripts
      return;
    }

    // Update current transcript
    this.currentTranscript = transcript;

    // Update FormControl if provided
    if (this.targetControl) {
      this.targetControl.setValue(transcript);
      this.targetControl.markAsDirty();

      // Trigger change detection for reactive forms
      this.cdr.detectChanges();

      // Dispatch input event for native elements
      const element = document.activeElement;
      if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) {
        const inputEvent = new Event('input', { bubbles: true });
        element.dispatchEvent(inputEvent);
      }
    }

    // Emit event
    if (isFinal) {
      this.transcriptReceived.emit(transcript);
    }
  }

  /**
   * Update processed transcript (LLM output)
   */
  private updateProcessedTranscript(processedTranscript: string, rawTranscript: string): void {
    // Always update with processed transcript (LLM output takes precedence)
    this.currentTranscript = processedTranscript;

    // Update FormControl if provided
    if (this.targetControl) {
      this.targetControl.setValue(processedTranscript);
      this.targetControl.markAsDirty();
      this.cdr.detectChanges();

      // Dispatch input event
      const element = document.activeElement;
      if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) {
        const inputEvent = new Event('input', { bubbles: true });
        element.dispatchEvent(inputEvent);
      }
    }

    // Emit event
    this.processedTranscriptReceived.emit(processedTranscript);
  }

  /**
   * Handle error
   */
  private handleError(error: StreamingSpeechError): void {
    console.error('❌ Streaming dictation error:', error);

    this.errorMessage = error.error;
    this.errorOccurred.emit(error);

    // If error is not recoverable, stop recording
    if (!error.recoverable) {
      this.isRecording = false;
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle warning
   */
  private handleWarning(warning: any): void {
    console.warn('⚠️ Streaming dictation warning:', warning);

    this.warningMessage = warning.message;

    // Auto-dismiss warning after 5 seconds
    setTimeout(() => {
      this.warningMessage = null;
      this.cdr.detectChanges();
    }, 5000);

    this.cdr.detectChanges();
  }

  /**
   * Get button text
   */
  getButtonText(): string {
    if (this.isConnecting) {
      return 'Connecting...';
    }
    if (this.isRecording) {
      return 'Stop Recording';
    }
    return 'Start Recording';
  }

  /**
   * Get button icon
   */
  getButtonIcon(): string {
    if (this.isRecording) {
      return 'stop';
    }
    return 'mic';
  }

  /**
   * Check if component is ready
   */
  get isReady(): boolean {
    return this.connectionStatus === 'connected' && !this.isConnecting;
  }
}
